import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    document.addEventListener('click', this.#handleDocumentClick);
    document.addEventListener('keydown', this.#handleDocumentKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    document.removeEventListener('click', this.#handleDocumentClick);
    document.removeEventListener('keydown', this.#handleDocumentKeydown);
    clearTimeout(this.#deactivateTimer);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
    if (this.#state.activeItem) {
      const submenu = findSubmenu(this.#state.activeItem);
      this.#updateWrappedMegaMenuColumns(submenu);
      this.#alignCompactSubmenu(this.#state.activeItem, submenu);
    }
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
    pinned: false,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #deactivateTimer;

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    clearTimeout(this.#deactivateTimer);

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.#updateWrappedMegaMenuColumns(submenu);
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#alignCompactSubmenu(item, submenu);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    } else {
      this.#updateWrappedMegaMenuColumns(submenu);
      finalHeight = submenu.offsetHeight || finalHeight;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#updateWrappedMegaMenuColumns(submenu ?? null);
        this.#alignCompactSubmenu(item, submenu ?? null);
        if (submenu) {
          const measuredHeight = submenu.offsetHeight;
          if (measuredHeight > 0 && measuredHeight !== finalHeight) {
            this.headerComponent?.style.setProperty('--submenu-height', `${measuredHeight}px`);
            this.#setFullOpenHeaderHeight(measuredHeight);
          }
        }
      });
    });
  };

  /**
   * Toggle a submenu open/closed on click for placeholder top-level links (no destination URL).
   * @param {MouseEvent} event
   */
  toggleMenu = (event) => {
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('[ref="menuitem"]');
    if (!(link instanceof HTMLElement) || !link.hasAttribute('aria-haspopup')) return;

    const toggleOnly =
      link.dataset.menuToggleOnly === 'true' ||
      link.getAttribute('role') === 'button';

    if (!toggleOnly) return;

    event.preventDefault();

    if (this.#state.pinned && this.#state.activeItem === link) {
      this.#state.pinned = false;
      this.#deactivate(link);
      return;
    }

    this.#state.pinned = true;
    clearTimeout(this.#deactivateTimer);
    this.activate(event);
  };

  /**
   * Keep the active submenu open while the pointer is over the panel.
   */
  keepActive = () => {
    clearTimeout(this.#deactivateTimer);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    if (this.#state.pinned && event.type !== 'click') {
      return;
    }

    const activeLink = this.#state.activeItem;
    const submenu = findSubmenu(activeLink);
    const listItem = activeLink?.closest('.menu-list__list-item');

    if (event instanceof PointerEvent && event.relatedTarget instanceof Node) {
      if (submenu?.contains(event.relatedTarget)) return;
      if (listItem?.contains(event.relatedTarget)) return;
    }

    const menu = submenu;
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    if (event.type === 'pointerleave') {
      clearTimeout(this.#deactivateTimer);
      this.#deactivateTimer = setTimeout(() => {
        const activeSubmenu = findSubmenu(this.#state.activeItem);
        if (this.#state.pinned) return;
        if (activeSubmenu?.matches(':hover')) return;
        if (this.#state.activeItem?.matches(':hover')) return;
        if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;
        this.#deactivate();
      }, 250);
      return;
    }

    this.#deactivate();
  }

  /**
   * Close pinned menus when clicking outside the header menu.
   * @param {MouseEvent} event
   */
  #handleDocumentClick = (event) => {
    if (!this.#state.pinned || !(event.target instanceof Node)) return;
    if (this.contains(event.target)) return;
    if (this.overflowMenu?.contains(event.target)) return;

    this.#state.pinned = false;
    this.#deactivate();
  };

  /**
   * Close pinned menus on Escape.
   * @param {KeyboardEvent} event
   */
  #handleDocumentKeydown = (event) => {
    if (event.key !== 'Escape' || !this.#state.pinned) return;

    this.#state.pinned = false;
    this.#deactivate();
  };

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    clearTimeout(this.#deactivateTimer);

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    const submenuBeforeClose = findSubmenu(item);
    if (submenuBeforeClose?.matches(':hover') && !this.#state.pinned) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.#state.pinned = false;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
      this.#clearCompactSubmenuAlignment(submenu);
    }
  };

  /**
   * Position single-column compact submenus under their parent nav link.
   * @param {HTMLElement | null} item
   * @param {HTMLElement | null | undefined} submenu
   */
  #alignCompactSubmenu(item, submenu) {
    this.querySelectorAll('.menu-list__submenu-inner').forEach((inner) => {
      if (!submenu?.contains(inner)) {
        inner.style.removeProperty('--submenu-content-offset-x');
      }
    });

    if (!item || !submenu) return;

    const listItem = item.closest('.menu-list__list-item');
    if (listItem?.slot === 'overflow') return;

    const inner = /** @type {HTMLElement | null} */ (submenu.querySelector('.menu-list__submenu-inner'));
    if (!inner?.querySelector('.mega-menu__grid--under-parent')) {
      inner?.style.removeProperty('--submenu-content-offset-x');
      return;
    }

    const link = item.matches('[ref="menuitem"]') ? item : item.querySelector('[ref="menuitem"]');
    if (!(link instanceof HTMLElement)) return;

    const linkRect = link.getBoundingClientRect();
    const innerRect = inner.getBoundingClientRect();
    const paddingLeft = Number.parseFloat(getComputedStyle(inner).paddingLeft) || 0;
    const offsetX = linkRect.left + linkRect.width / 2 - innerRect.left - paddingLeft;

    inner.style.setProperty('--submenu-content-offset-x', `${offsetX}px`);
  }

  /**
   * @param {HTMLElement} submenu
   */
  #clearCompactSubmenuAlignment(submenu) {
    submenu.querySelector('.menu-list__submenu-inner')?.style.removeProperty('--submenu-content-offset-x');
  }

  /**
   * Hide child links in mega menu columns that have moved below the first row.
   * This keeps narrow desktop widths from creating tall, awkward second-row columns.
   * @param {HTMLElement | null | undefined} submenu
   */
  #updateWrappedMegaMenuColumns(submenu) {
    if (!submenu) return;

    submenu.querySelectorAll('.mega-menu__list').forEach((list) => {
      const columns = [...list.querySelectorAll(':scope > .mega-menu__column')].filter(
        (column) => column instanceof HTMLElement
      );

      if (columns.length === 0) return;

      const firstRowTop = Math.min(...columns.map((column) => column.getBoundingClientRect().top));

      columns.forEach((column) => {
        if (!(column instanceof HTMLElement)) return;

        const hasChildLinks = Boolean(column.querySelector('.mega-menu__sublinks-grid'));
        const isWrapped = column.getBoundingClientRect().top > firstRowTop + 2;

        if (hasChildLinks && isWrapped) {
          column.dataset.hideWrappedSublinks = 'true';
        } else {
          delete column.dataset.hideWrappedSublinks;
        }
      });
    });
  }

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element.matches('[ref="menuitem"]')) return element;

  const closestItem = element.closest('[ref="menuitem"]');
  if (closestItem instanceof HTMLElement) return closestItem;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
